// The reason every tap carries: a chip from a fixed list, or a few words of the grown-up's own.
// Good behaviour gets as many chips as bad, on purpose.

export const POSITIVE_REASONS = ["kind", "helped", "listened", "tidy", "tried hard", "shared"] as const;
export const NEGATIVE_REASONS = ["rude", "didn't listen", "hit", "lied", "unkind", "meltdown"] as const;

export type Reason = (typeof POSITIVE_REASONS)[number] | (typeof NEGATIVE_REASONS)[number];

export function isReason(s: unknown): s is Reason {
  return (
    typeof s === "string" &&
    ((POSITIVE_REASONS as readonly string[]).includes(s) || (NEGATIVE_REASONS as readonly string[]).includes(s))
  );
}

/** The chips that go with a plus tap or a minus tap. */
export function reasonsFor(delta: number): readonly Reason[] {
  return delta > 0 ? POSITIVE_REASONS : NEGATIVE_REASONS;
}

/** The most a typed reason may be: a few words, like a chip, not a note. */
export const MAX_REASON_LENGTH = 40;

/**
 * The reason a tap will be saved with: null for none, the tidied text for a chip of the tap's
 * own sign or a reason typed in, or undefined when it is not allowed. A typed reason may say
 * anything up to MAX_REASON_LENGTH characters, except a chip of the other sign: a minus tap is
 * never saved as "kind".
 */
export function cleanReason(raw: unknown, delta: number): string | null | undefined {
  if (raw === undefined || raw === null) return null;
  if (typeof raw !== "string") return undefined;
  // Control characters (a pasted newline, say) become spaces; runs of space become one.
  const text = raw.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
  if (text === "") return null;
  if ([...text].length > MAX_REASON_LENGTH) return undefined;
  const otherSign = reasonsFor(-delta) as readonly string[];
  if (otherSign.includes(text.toLowerCase())) return undefined;
  return text;
}
